// Staleness for the Daily Update List. Deliberately tighter than the matrix's
// freshness() (amber >=4d / red >=8d): this list tracks a DAILY cadence, so
// green means "touched within a day". Palette hexes match the matrix.
const DAY_MS = 24 * 60 * 60 * 1000;
const GREEN = '#157F4A';
const AMBER = '#B26B00';
const RED = '#C0392B';

// 'overdue' covers both never-priced and >=4d-stale rows: for a daily-cadence
// queue they are the same "needs attention now" class.
export type StalenessBucket = 'overdue' | 'aging' | 'fresh';

export interface WorklistStaleness {
  label: string;
  color: string;
  bucket: StalenessBucket;
}

export function worklistStaleness(lastPriceUpdateAt: string | null, now: number = Date.now()): WorklistStaleness {
  const time = lastPriceUpdateAt ? new Date(lastPriceUpdateAt).getTime() : NaN;
  if (Number.isNaN(time)) return { label: 'Never', color: RED, bucket: 'overdue' };

  const days = Math.max(0, Math.floor((now - time) / DAY_MS));
  const label = days === 0 ? 'today' : days === 1 ? 'yesterday' : `${days}d ago`;
  const bucket: StalenessBucket = days >= 4 ? 'overdue' : days >= 2 ? 'aging' : 'fresh';
  const color = bucket === 'overdue' ? RED : bucket === 'aging' ? AMBER : GREEN;

  return { label, color, bucket };
}

const BUCKET_WEIGHT: Record<StalenessBucket, number> = { overdue: 0, aging: 1, fresh: 2 };

export type Ranked<T> = T & { rank: number };

// Membership is top-N by inquiry count (the BE's array order — rank preserves
// it); DISPLAY order sorts by staleness BUCKET (overdue, then aging, then
// fresh), with rank breaking ties inside each bucket. Bucket-first — not
// exact-date-first — so a wall of never-priced rows can't bury the most
// popular overdue family at the bottom of its own queue.
export function orderWorklist<T extends { lastPriceUpdateAt: string | null }>(
  rows: T[],
  now: number = Date.now()
): Ranked<T>[] {
  const ranked = rows.map((row, index) => ({ ...row, rank: index + 1 }));

  return ranked.sort((a, b) => {
    const weightDiff =
      BUCKET_WEIGHT[worklistStaleness(a.lastPriceUpdateAt, now).bucket] -
      BUCKET_WEIGHT[worklistStaleness(b.lastPriceUpdateAt, now).bucket];
    if (weightDiff !== 0) return weightDiff;
    return a.rank - b.rank;
  });
}
